import React from 'react';
// material-ui
import { Theme } from '@mui/material/styles';
import { CheckboxProps } from '@mui/material/Checkbox';

// project import
import getColors from '@assetron/common/utils/getColors';

// assets
import BorderOutlined from '@ant-design/icons/BorderOutlined';
import CheckSquareFilled from '@ant-design/icons/CheckSquareFilled';
import MinusSquareFilled from '@ant-design/icons/MinusSquareFilled';

// types
import { ExtendedStyleProps } from '@assetron/common/types/extended';

// ==============================|| RADIO - COLORS ||============================== //

function getColorStyle({ color, theme }: ExtendedStyleProps) {
  const colors = getColors(theme, color);
  const { lighter, main, dark } = colors;

  return {
    '&:hover': {
      backgroundColor: lighter,
      '& .icon': {
        borderColor: main,
      },
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${dark}`,
      outlineOffset: -4,
    },
  };
}

// ==============================|| CHECKBOX - SIZE STYLE ||============================== //

interface CheckboxSizeProps {
  fontSize: string;
  width: string;
  height: string;
}

function getSizeStyle(size?: CheckboxProps['size']): CheckboxSizeProps {
  switch (size) {
    case 'small':
      return { fontSize: '18px', width: '18px', height: '18px' };
    case 'large':
      return { fontSize: '26px', width: '26px', height: '26px' };
    case 'medium':
    default:
      return { fontSize: '22px', width: '22px', height: '22px' };
  }
}

// ==============================|| CHECKBOX - STYLE ||============================== //

function checkboxStyle(size?: CheckboxProps['size']) {
  const sizes: CheckboxSizeProps = getSizeStyle(size);

  return {
    '& .icon': {
      fontSize: `${sizes.fontSize} !important`,
      width: `${sizes.width} !important`,
      height: `${sizes.height} !important`,
    },
  };
}

// ==============================|| OVERRIDES - CHECKBOX ||============================== //

export default function Checkbox(theme: Theme) {
  const { palette } = theme;

  return {
    MuiCheckbox: {
      defaultProps: {
        className: 'size-small',
        icon: <BorderOutlined className="icon" />,
        checkedIcon: <CheckSquareFilled className="icon" />,
        indeterminateIcon: <MinusSquareFilled className="icon" />,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          color: palette.secondary[300],
          width: 'auto !important',
          height: 'auto !important',
          // Prevent browser font size changes from affecting checkbox dimensions
          fontSize: '22px !important',
          '& .MuiSvgIcon-root': {
            fontSize: 'inherit !important',
            width: '1em !important',
            height: '1em !important',
          },
          '&.size-small': {
            ...checkboxStyle('small'),
            fontSize: '18px !important',
          },
          '&.size-medium': {
            ...checkboxStyle('medium'),
            fontSize: '22px !important',
          },
          '&.size-large': {
            ...checkboxStyle('large'),
            fontSize: '26px !important',
          },
        },
        colorPrimary: getColorStyle({ color: 'primary', theme }),
        colorSecondary: getColorStyle({ color: 'secondary', theme }),
        colorSuccess: getColorStyle({ color: 'success', theme }),
        colorWarning: getColorStyle({ color: 'warning', theme }),
        colorInfo: getColorStyle({ color: 'info', theme }),
        colorError: getColorStyle({ color: 'error', theme }),
      },
    },
  };
}
